/**
 * Inventory Module Landing Page - Storekeeper Dashboard
 * AS9100D-Compliant Inventory Management Command Center
 * 
 * Sprint 7: Dashboard Implementation
 * Sprint 7.1: WebSocket Real-time Updates
 * Sprint 7.2: Role-based Dashboard Routing
 * 
 * Features:
 * - 6 Summary Cards (Pending Receipts, Issues Today, Counts Due, QC Hold, Quarantine, Expiring)
 * - Personalized Task List with priorities
 * - Quick Actions
 * - Recent Activity Feed
 * - AS9100D Compliance Indicators
 * - Real-time WebSocket Updates
 * - Role-based Dashboard Selector
 * 
 * @version 3.2.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Package, 
  MapPin, 
  ClipboardList, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  AlertTriangle,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Boxes,
  Warehouse,
  Shield,
  Calendar,
  Search,
  ChevronRight,
  Timer,
  Ban,
  Activity,
  ListTodo,
  ArrowRight,
  Wifi,
  WifiOff,
  Sliders,
  Building,
  LayoutGrid
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import { useInventoryWebSocket } from '../hooks/useInventoryWebSocket';
import { DashboardSelector } from '../hooks/useInventoryDashboard';
import { PartChangeNotificationPanel } from '../components/PartChangeNotificationPanel';
import InventoryLayout from '../components/layouts/InventoryLayout';

/**
 * Summary Card Component - Main Dashboard Metric Cards
 * Per design spec: 6 cards in 2 rows
 */
const SummaryCard = ({ 
  title, 
  count, 
  subtitle, 
  icon: Icon, 
  color = 'blue',
  urgent = false,
  onClick 
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'bg-blue-100 text-blue-600',
      border: urgent ? 'border-blue-400' : 'border-blue-100',
      badge: 'bg-blue-100 text-blue-700'
    },
    green: {
      bg: 'bg-green-50',
      icon: 'bg-green-100 text-green-600',
      border: 'border-green-100',
      badge: 'bg-green-100 text-green-700'
    },
    amber: {
      bg: 'bg-amber-50',
      icon: 'bg-amber-100 text-amber-600',
      border: urgent ? 'border-amber-400' : 'border-amber-100',
      badge: 'bg-amber-100 text-amber-700'
    },
    red: {
      bg: 'bg-red-50',
      icon: 'bg-red-100 text-red-600',
      border: urgent ? 'border-red-400 border-2' : 'border-red-100',
      badge: 'bg-red-100 text-red-700'
    },
    purple: {
      bg: 'bg-purple-50',
      icon: 'bg-purple-100 text-purple-600',
      border: 'border-purple-100',
      badge: 'bg-purple-100 text-purple-700'
    },
    slate: {
      bg: 'bg-slate-50',
      icon: 'bg-slate-100 text-slate-600',
      border: 'border-slate-100',
      badge: 'bg-slate-100 text-slate-700'
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <Card 
      className={`p-4 ${colors.bg} border ${colors.border} cursor-pointer hover:shadow-md transition-all group`}
      onClick={onClick}
      data-testid={`summary-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colors.icon}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-gray-600">{title}</span>
          </div>
          <p className="text-3xl font-bold text-[#101957] mt-2">{count}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
    </Card>
  );
};

/**
 * Task Item Component - For personalized task list
 */
const TaskItem = ({ task, onAction }) => {
  const priorityConfig = {
    CRITICAL: {
      color: 'bg-red-100 text-red-700 border-red-200',
      icon: <Ban className="w-3 h-3" />,
      label: '🔴'
    },
    HIGH: {
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: <AlertTriangle className="w-3 h-3" />,
      label: '🟠'
    },
    MEDIUM: {
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: <AlertCircle className="w-3 h-3" />,
      label: '🟡'
    },
    LOW: {
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: <CheckCircle className="w-3 h-3" />,
      label: '🟢'
    }
  };

  const config = priorityConfig[task.priority] || priorityConfig.MEDIUM;

  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-lg border ${task.is_overdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'} hover:shadow-sm transition-shadow cursor-pointer`}
      onClick={() => onAction(task.action_url)}
      data-testid={`task-item-${task.reference_number}`}
    >
      <span className="text-lg">{config.label}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${task.is_overdue ? 'text-red-700' : 'text-gray-900'}`}>
          {task.title}
        </p>
        <p className="text-xs text-gray-500 truncate">{task.subtitle}</p>
      </div>
      <div className="flex items-center gap-2">
        {task.is_overdue ? (
          <Badge variant="destructive" className="text-xs">Overdue</Badge>
        ) : task.due_date ? (
          <span className="text-xs text-gray-400">
            {formatIST(task.due_date)}
          </span>
        ) : null}
        <Button size="sm" variant="ghost" className="h-7 px-2">
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Activity Item Component - For recent activity feed
 */
const ActivityItem = ({ activity }) => {
  const typeIcons = {
    // Transaction types
    RECEIPT: <ArrowDownToLine className="w-3.5 h-3.5 text-green-600" />,
    ISSUE: <ArrowUpFromLine className="w-3.5 h-3.5 text-blue-600" />,
    TRANSFER: <ArrowRight className="w-3.5 h-3.5 text-purple-600" />,
    QC_RELEASE: <CheckCircle className="w-3.5 h-3.5 text-green-600" />,
    QC_REJECT: <AlertTriangle className="w-3.5 h-3.5 text-red-600" />,
    ADJUSTMENT_PLUS: <Package className="w-3.5 h-3.5 text-green-600" />,
    ADJUSTMENT_MINUS: <Package className="w-3.5 h-3.5 text-amber-600" />,
    QUARANTINE_IN: <AlertTriangle className="w-3.5 h-3.5 text-red-600" />,
    SCRAP: <Ban className="w-3.5 h-3.5 text-red-600" />,
    // Audit event types (warehouse/zone/location)
    WAREHOUSE_CREATED: <Building className="w-3.5 h-3.5 text-indigo-600" />,
    WAREHOUSE_UPDATED: <Building className="w-3.5 h-3.5 text-indigo-500" />,
    ZONE_CREATED: <LayoutGrid className="w-3.5 h-3.5 text-teal-600" />,
    ZONE_UPDATED: <LayoutGrid className="w-3.5 h-3.5 text-teal-500" />,
    LOCATION_CREATED: <MapPin className="w-3.5 h-3.5 text-amber-600" />,
    LOCATION_UPDATED: <MapPin className="w-3.5 h-3.5 text-amber-500" />
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Support both old (transaction_type) and new (activity_type) field names
  const activityType = activity.activity_type || activity.transaction_type;

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 p-1.5 rounded-full bg-gray-100">
        {typeIcons[activityType] || <Activity className="w-3.5 h-3.5 text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700">{activity.message}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {formatTime(activity.performed_at)} • {activity.performed_by_name}
        </p>
      </div>
    </div>
  );
};

/**
 * Quick Action Button Component
 */
const QuickAction = ({ title, icon: Icon, description, onClick, disabled, badge }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-start gap-4 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#101957] hover:shadow-md transition-all text-left w-full ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="p-3 rounded-lg bg-[#101957]/5">
      <Icon className="w-6 h-6 text-[#101957]" />
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-[#101957]">{title}</h3>
        {badge && <Badge variant="outline" className="text-xs">{badge}</Badge>}
      </div>
      <p className="text-sm text-gray-500 mt-0.5">{description}</p>
    </div>
  </button>
);

/**
 * Main Inventory Page Component - Storekeeper Dashboard
 */
const InventoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Handle dashboard switch
  const handleDashboardSwitch = (path) => {
    navigate(path);
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Load dashboard data
  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const response = await api.get('/api/v1/inventory/v2/dashboard/storekeeper');
      setDashboardData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading dashboard:', err);
      // Fallback to basic dashboard if enhanced not available
      try {
        const basicResponse = await api.get('/api/v1/inventory/v2/dashboard');
        // Transform basic data to match enhanced structure
        setDashboardData({
          summary_cards: {
            pending_receipts: { count: 0, urgent: 0, subtitle: 'No pending receipts' },
            issues_today: { count: basicResponse.data.issues_today || 0, pending: 0, completed: 0, subtitle: '' },
            counts_due: { count: basicResponse.data.pending_cycle_counts || 0, this_week: 0, overdue: 0, subtitle: '' },
            qc_hold: { count: basicResponse.data.lots_in_qc_hold || 0, oldest_days: 0, subtitle: '' },
            quarantine: { count: basicResponse.data.lots_in_quarantine || 0, counterfeit: 0, subtitle: '' },
            expiring_soon: { count: basicResponse.data.lots_expiring_30_days || 0, in_30_days: basicResponse.data.lots_expiring_30_days || 0, subtitle: '' }
          },
          tasks: { total_tasks: 0, critical_count: 0, high_priority_count: 0, tasks: [] },
          recent_activity: [],
          metrics: {
            total_parts_in_stock: basicResponse.data.total_parts_in_stock || 0,
            total_lots: basicResponse.data.total_lots || 0,
            total_locations: basicResponse.data.total_locations || 0,
            receipts_today: basicResponse.data.receipts_today || 0,
            issues_today: basicResponse.data.issues_today || 0
          },
          generated_at: basicResponse.data.generated_at
        });
        setLastRefresh(new Date());
      } catch (fallbackErr) {
        setError('Failed to load dashboard data');
        // Set empty state
        setDashboardData({
          summary_cards: {
            pending_receipts: { count: 0, urgent: 0, subtitle: 'No data' },
            issues_today: { count: 0, pending: 0, completed: 0, subtitle: '' },
            counts_due: { count: 0, this_week: 0, overdue: 0, subtitle: '' },
            qc_hold: { count: 0, oldest_days: 0, subtitle: '' },
            quarantine: { count: 0, counterfeit: 0, subtitle: '' },
            expiring_soon: { count: 0, in_30_days: 0, subtitle: '' }
          },
          tasks: { total_tasks: 0, critical_count: 0, high_priority_count: 0, tasks: [] },
          recent_activity: [],
          metrics: { total_parts_in_stock: 0, total_lots: 0, total_locations: 0 },
          generated_at: new Date().toISOString()
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // WebSocket for real-time updates
  const { isConnected, eventCount } = useInventoryWebSocket({
    enabled: true,
    onRefresh: () => loadDashboard(true), // Silent refresh
    showToasts: true
  });

  const handleRefresh = async () => {
    await loadDashboard();
    toast.success('Dashboard refreshed');
  };

  const handleTaskAction = (url) => {
    if (url) {
      navigate(url);
    }
  };

  // Loading state
  if (isLoading && !dashboardData) {
    return (
      <InventoryLayout>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#101957] mx-auto mb-4" />
            <p className="text-gray-500">Loading inventory dashboard...</p>
          </div>
        </div>
      </InventoryLayout>
    );
  }

  const summary = dashboardData?.summary_cards || {};
  const tasks = dashboardData?.tasks || { tasks: [], total_tasks: 0 };
  const activity = dashboardData?.recent_activity || [];
  const metrics = dashboardData?.metrics || {};

  return (
    <InventoryLayout>
      <div className="max-w-6xl mx-auto px-6 py-6" data-testid="inventory-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-[#101957]">Stores Dashboard</h1>
                {/* Dashboard Selector for multi-role users */}
                <DashboardSelector 
                  onSelect={handleDashboardSwitch}
                  currentPath={location.pathname}
                />
              </div>
              <p className="text-sm text-gray-500">{getGreeting()}. Here is your workload for today.</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* WebSocket Connection Status */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge 
                    variant="outline" 
                    className={`gap-1 ${isConnected 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : 'bg-gray-50 text-gray-500 border-gray-200'}`}
                    data-testid="websocket-status"
                  >
                    {isConnected ? (
                      <Wifi className="w-3 h-3" />
                    ) : (
                      <WifiOff className="w-3 h-3" />
                    )}
                    {isConnected ? 'Live' : 'Offline'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    {isConnected 
                      ? `Real-time updates active (${eventCount} events received)` 
                      : 'Real-time updates disconnected'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            {lastRefresh && (
              <span className="text-xs text-gray-400">
                Updated {formatIST(lastRefresh)}
              </span>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              data-testid="refresh-dashboard"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards - 6 cards in 2 rows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            title="Pending Receipts"
            count={summary.pending_receipts?.count || 0}
            subtitle={summary.pending_receipts?.subtitle}
            icon={ArrowDownToLine}
            color={(summary.pending_receipts?.urgent || 0) > 0 ? 'red' : 'blue'}
            urgent={(summary.pending_receipts?.urgent || 0) > 0}
            onClick={() => navigate('/inventory/receiving')}
          />
          <SummaryCard
            title="Issues Today"
            count={summary.issues_today?.count || 0}
            subtitle={summary.issues_today?.subtitle || `${summary.issues_today?.pending || 0} pending`}
            icon={ArrowUpFromLine}
            color="purple"
            onClick={() => navigate('/inventory/issue-transfer')}
          />
          <SummaryCard
            title="Counts Due"
            count={summary.counts_due?.count || 0}
            subtitle={summary.counts_due?.subtitle}
            icon={ClipboardList}
            color={(summary.counts_due?.overdue || 0) > 0 ? 'red' : 'amber'}
            urgent={(summary.counts_due?.overdue || 0) > 0}
            onClick={() => navigate('/inventory/cycle-counts')}
          />
          <SummaryCard
            title="QC Hold Items"
            count={summary.qc_hold?.count || 0}
            subtitle={summary.qc_hold?.subtitle}
            icon={Clock}
            color="amber"
            onClick={() => navigate('/inventory/receiving?tab=qc')}
          />
          <SummaryCard
            title="Quarantine"
            count={summary.quarantine?.count || 0}
            subtitle={summary.quarantine?.subtitle}
            icon={AlertTriangle}
            color={(summary.quarantine?.counterfeit || 0) > 0 ? 'red' : 'red'}
            urgent={(summary.quarantine?.counterfeit || 0) > 0}
            onClick={() => navigate('/inventory/quarantine')}
          />
          <SummaryCard
            title="Expiring Soon"
            count={summary.expiring_soon?.count || 0}
            subtitle={summary.expiring_soon?.subtitle || `${summary.expiring_soon?.in_30_days || 0} in 30 days`}
            icon={Timer}
            color={(summary.expiring_soon?.in_30_days || 0) > 5 ? 'amber' : 'slate'}
            onClick={() => navigate('/inventory/stock?expiry=90')}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Tasks and Quick Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Task List */}
            <Card className="p-5" data-testid="task-list-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-[#101957]" />
                  <h2 className="text-lg font-semibold text-[#101957]">My Tasks Today</h2>
                  {tasks.critical_count > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {tasks.critical_count} Critical
                    </Badge>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="text-xs">
                  See All
                </Button>
              </div>
              
              <div className="space-y-2">
                {tasks.tasks?.length > 0 ? (
                  tasks.tasks.slice(0, 6).map((task, idx) => (
                    <TaskItem 
                      key={task.reference_id || idx} 
                      task={task} 
                      onAction={handleTaskAction}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" />
                    <p>No pending tasks. Great work</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-5" data-testid="quick-actions-card">
              <h2 className="text-lg font-semibold text-[#101957] mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <QuickAction
                  title="New Receipt"
                  icon={ArrowDownToLine}
                  description="Create goods receipt note (GRN)"
                  onClick={() => navigate('/inventory/receiving?action=new')}
                />
                <QuickAction
                  title="New Issue"
                  icon={ArrowUpFromLine}
                  description="Issue stock to work order or transfer"
                  onClick={() => navigate('/inventory/issue-transfer?action=new-issue')}
                />
                <QuickAction
                  title="Transfer Stock"
                  icon={ArrowRight}
                  description="Move inventory between locations"
                  onClick={() => navigate('/inventory/issue-transfer?tab=transfer')}
                />
                <QuickAction
                  title="Start Count"
                  icon={ClipboardList}
                  description="Begin a cycle count"
                  onClick={() => navigate('/inventory/cycle-counts?action=new')}
                />
              </div>
            </Card>
          </div>

          {/* Right Column - Activity & Info */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <Card className="p-5" data-testid="recent-activity-card">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-[#101957]" />
                <h2 className="text-lg font-semibold text-[#101957]">Recent Activity</h2>
              </div>
              
              <div className="divide-y divide-gray-100">
                {activity?.length > 0 ? (
                  activity.slice(0, 8).map((item, idx) => (
                    <ActivityItem key={item.activity_id || item.transaction_id || idx} activity={item} />
                  ))
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Key Metrics */}
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wide">Key Metrics</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Parts in Stock</span>
                  <span className="font-semibold text-[#101957]">{metrics.total_parts_in_stock || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Active Lots</span>
                  <span className="font-semibold text-[#101957]">{metrics.total_lots || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Storage Locations</span>
                  <span className="font-semibold text-[#101957]">{metrics.total_locations || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Receipts Today</span>
                  <span className="font-semibold text-green-600">{metrics.receipts_today || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Issues Today</span>
                  <span className="font-semibold text-blue-600">{metrics.issues_today || 0}</span>
                </div>
              </div>
            </Card>

            {/* Part Configuration Changes Panel (AS9100D 8.1.2) */}
            <PartChangeNotificationPanel 
              showComplianceInfo={true}
              defaultExpanded={false}
            />

            {/* Last Updated */}
            {dashboardData?.generated_at && (
              <p className="text-xs text-center text-gray-400">
                Data as of: {formatIST(dashboardData.generated_at)}
              </p>
            )}
          </div>
        </div>
      </div>
    </InventoryLayout>
  );
};

export default InventoryPage;
